import { createContext, ReactNode, useContext, useReducer } from 'react';
import { useController, useFetch, useSuspense } from 'rest-hooks';
import { ApplicationXmlResource } from '../resources/applicationXmlResource';
import { LOCAL_STORAGE_KEYS } from '../localStorageKeys';
import { InputSourceModes } from '../../modules/layout/inputBlock/inputSourceButtons';

type Action = { type: 'SET_APPLICATION'; value: Partial<State> } | { type: 'RESET_APPLICATION' };
type Dispatch = (action: Action) => void;
type State = {
  applicationSource: InputSourceModes | undefined;
  applicationNumber: string | undefined;
  applicationXml: string | undefined;
  applicationXmlFilename: string | undefined;
};

const ApplicationContext = createContext<{ state: State; dispatch: Dispatch } | undefined>(
  undefined
);

function applicationReducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_APPLICATION': {
      if (action.value.applicationSource) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.inputSource.mode, action.value.applicationSource);
      }
      if (action.value.applicationNumber) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.inputSource.applicationNumber,
          action.value.applicationNumber
        );
      }
      if (action.value.applicationXml) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.inputSource.xmlContent,
          action.value.applicationXml
        );
      }
      if (action.value.applicationXmlFilename) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.inputSource.xmlFilename,
          action.value.applicationXmlFilename
        );
      }
      return { ...state, ...action.value };
    }
    case 'RESET_APPLICATION': {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.inputSource.mode);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.inputSource.applicationNumber);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.inputSource.xmlContent);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.inputSource.xmlFilename);
      return {
        applicationSource: undefined,
        applicationNumber: undefined,
        applicationXml: undefined,
        applicationXmlFilename: undefined
      };
    }
    default: {
      throw new Error(`Unhandled action!`);
    }
  }
}

interface ApplicationProviderProps {
  children: ReactNode;
}
export function ApplicationProvider(props: ApplicationProviderProps) {
  const { children } = props;

  const localApplicationSource = localStorage.getItem(LOCAL_STORAGE_KEYS.inputSource.mode);

  const localApplicationFilename = localStorage.getItem(LOCAL_STORAGE_KEYS.inputSource.xmlFilename);

  const localApplicationNumber = localStorage.getItem(
    LOCAL_STORAGE_KEYS.inputSource.applicationNumber
  );
  const localXml = localApplicationNumber
    ? localStorage.getItem(LOCAL_STORAGE_KEYS.inputSource.xmlContent)
    : null;

  const applicationXml = useSuspense(
    ApplicationXmlResource.detail(),
    localApplicationNumber && !localXml && localApplicationSource === 'Dploy'
      ? {
          applicationNumber: localApplicationNumber
        }
      : null
  );

  const initState = {
    applicationSource: localApplicationSource,
    applicationNumber: localApplicationNumber ?? undefined,
    applicationXml: localXml ?? applicationXml?.xml.firstChild?.textContent ?? undefined,
    applicationXmlFilename: localApplicationFilename ?? undefined
  } as State as never;
  const [state, dispatch] = useReducer(applicationReducer, initState);
  const value = { state, dispatch };
  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}

export const useApplication = () => {
  const context = useContext(ApplicationContext);
  if (context == undefined) {
    throw new Error('useApplication must be used within a ApplicationProvider');
  }

  const { fetch } = useController();

  const resetApplication = () => context.dispatch({ type: 'RESET_APPLICATION' });

  const setApplication = async (
    applicationNumber: string,
    applicationSourceMode: InputSourceModes,
    applicationXmlFile?: { name: string; xml: string }
  ) => {
    const applicationXmlResult = {
      filename: applicationXmlFile?.name,
      xml: applicationXmlFile?.xml
    };

    if (applicationSourceMode === 'Dploy' && applicationNumber) {
      //Fetch ApplicationXml
      const fetchedXml = await fetch(ApplicationXmlResource.detail(), {
        applicationNumber: applicationNumber
      });
      applicationXmlResult.xml = fetchedXml?.xml;
    }

    context.dispatch({
      type: 'SET_APPLICATION',
      value: {
        applicationSource: applicationSourceMode,
        applicationNumber: applicationNumber,
        applicationXml: applicationXmlResult.xml,
        applicationXmlFilename: applicationXmlResult.filename
      }
    });
  };
  return {
    applicationSource: context.state.applicationSource,
    applicationNumber: context.state.applicationNumber,
    applicationXml: context.state.applicationXml,
    applicationXmlFilename: context.state.applicationXmlFilename,
    setApplication,
    resetApplication
  };
};
